import React, { useEffect, useRef, useState } from "react";
import {
    Calendar,
    ChevronDown,
    Repeat,
    User,
    Flag,
    CornerDownLeft,
} from "lucide-react";
import { toast } from "react-toastify";
import { PRIORITY_OPTIONS, REPEAT_OPTIONS } from "../constants";
import { parseQuickAddInput, quickAddHintText } from "../utils/parseQuickAddInput";

function toDateInputValue(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function resolveDueDate(preset, customDate) {
    if (preset === "tomorrow") {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d;
    }
    if (preset === "custom" && customDate) {
        return new Date(customDate);
    }
    return new Date();
}

const TaskQuickAdd = ({
    role = "admin",
    apiUrl,
    token,
    assignOptions = [],
    onTaskCreated,
    defaultAssignee = null,
    inputRef: externalInputRef,
}) => {
    const internalRef = useRef(null);
    const inputRef = externalInputRef || internalRef;
    const [title, setTitle] = useState("");
    const [duePreset, setDuePreset] = useState("today");
    const [customDueDate, setCustomDueDate] = useState(toDateInputValue(new Date()));
    const [assignTo, setAssignTo] = useState(defaultAssignee);
    const [priority, setPriority] = useState("medium");
    const [repetition, setRepetition] = useState("none");
    const [submitting, setSubmitting] = useState(false);
    const [openPanel, setOpenPanel] = useState(null);
    const [parsedPreview, setParsedPreview] = useState(null);

    const handleTitleChange = (value) => {
        setTitle(value);
        const parsed = parseQuickAddInput(value);
        setParsedPreview(parsed.hadHints ? parsed : null);
    };

    useEffect(() => {
        if (defaultAssignee && !assignTo) setAssignTo(defaultAssignee);
    }, [defaultAssignee, assignTo]);

    useEffect(() => {
        if (assignOptions.length > 0 && !assignTo && role === "admin") {
            setAssignTo(assignOptions[0]);
        }
    }, [assignOptions, assignTo, role]);

    const resetForm = () => {
        setTitle("");
        setDuePreset("today");
        setCustomDueDate(toDateInputValue(new Date()));
        setPriority("medium");
        setRepetition("none");
        setOpenPanel(null);
        setParsedPreview(null);
        if (role === "admin" && assignOptions.length > 0) setAssignTo(assignOptions[0]);
    };

    const handleCreate = async () => {
        const raw = title.trim();
        if (!raw) {
            toast.error("Enter a task title");
            inputRef.current?.focus();
            return;
        }
        if (role === "admin" && assignOptions.length > 0 && !assignTo?.value) {
            toast.error("Select who to assign this task to");
            return;
        }

        const parsed = parseQuickAddInput(raw);
        const finalTitle = parsed.hadHints ? parsed.title.trim() : raw;
        if (!finalTitle) {
            toast.error(
                parsed.hadHints
                    ? "Add a task name (e.g. Restock bar tomorrow p1)"
                    : "Enter a task title"
            );
            inputRef.current?.focus();
            return;
        }
        const effectivePriority = parsed.hadHints ? parsed.priority : priority;
        const effectiveRepetition = parsed.hadHints && parsed.repetition !== "none" ? parsed.repetition : repetition;
        const effectiveDuePreset = parsed.hadHints ? parsed.duePreset : duePreset;
        const effectiveCustomDate = parsed.customDueDate || customDueDate;

        setSubmitting(true);
        try {
            const dueDate = resolveDueDate(effectiveDuePreset, effectiveCustomDate);
            const formData = new FormData();
            formData.append("title", finalTitle);
            formData.append("due_date", dueDate.toISOString());
            formData.append("start_date", new Date().toISOString());
            formData.append("priority", effectivePriority);
            formData.append("repetition", effectiveRepetition);
            formData.append("notes", "");
            if (role === "admin" && assignTo?.value) {
                formData.append("assign_to", assignTo.value);
            }

            const endpoint =
                role === "admin" ? `${apiUrl}/task` : `${apiUrl}/task/create-task`;
            const response = await fetch(endpoint, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            const res = await response.json();
            if (response.ok) {
                toast.success("Task added");
                resetForm();
                onTaskCreated?.(res.task);
                inputRef.current?.focus();
            } else {
                toast.error(res.error || res.message || "Failed to create task");
            }
        } catch (error) {
            toast.error(error.message || "Failed to create task");
        } finally {
            setSubmitting(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleCreate();
        }
        if (e.key === "Escape") setOpenPanel(null);
    };

    const priorityLabelData = PRIORITY_OPTIONS.find((p) => p.value === priority);
    const repeatLabel = REPEAT_OPTIONS.find((r) => r.value === repetition)?.label;
    const dueLabel =
        duePreset === "today"
            ? "Today"
            : duePreset === "tomorrow"
              ? "Tomorrow"
              : customDueDate
                ? new Date(customDueDate + "T12:00:00").toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                  })
                : "Pick date";

    return (
        <div className="rounded-xl border border-line bg-surface p-3 shadow-sm">
            <div className="flex items-start gap-2">
                <div className="mt-2.5 h-4 w-4 shrink-0 rounded-full border-2 border-ink-muted/50" />
                <div className="min-w-0 flex-1">
                    <input
                        ref={inputRef}
                        type="text"
                        value={title}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="What needs to be done? (try: tomorrow p1)"
                        disabled={submitting}
                        className="w-full bg-transparent text-sm font-medium text-ink placeholder:text-ink-muted outline-none"
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {["today", "tomorrow"].map((preset) => (
                            <button
                                key={preset}
                                type="button"
                                onClick={() => {
                                    setDuePreset(preset);
                                    setOpenPanel(null);
                                }}
                                className={`rounded-md border px-2 py-0.5 text-xs font-medium capitalize transition ${
                                    duePreset === preset
                                        ? "border-primary bg-primary-soft text-primary"
                                        : "border-line text-ink-secondary hover:bg-surface-2"
                                }`}
                            >
                                {preset}
                            </button>
                        ))}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setOpenPanel((p) => (p === "date" ? null : "date"))}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium transition ${
                                    duePreset === "custom"
                                        ? "border-primary bg-primary-soft text-primary"
                                        : "border-line text-ink-secondary hover:bg-surface-2"
                                }`}
                            >
                                <Calendar size={12} />
                                {duePreset === "custom" ? dueLabel : "Date"}
                            </button>
                            {openPanel === "date" && (
                                <div className="absolute left-0 top-full z-20 mt-1 rounded-lg border border-line bg-surface-2 p-2 shadow-lg">
                                    <input
                                        type="date"
                                        value={customDueDate}
                                        onChange={(e) => {
                                            setCustomDueDate(e.target.value);
                                            setDuePreset("custom");
                                        }}
                                        className="rounded-md border border-line bg-surface px-2 py-1 text-xs text-ink outline-none focus:border-primary [color-scheme:dark]"
                                    />
                                </div>
                            )}
                        </div>
                        {role === "admin" && assignOptions.length > 0 && (
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setOpenPanel((p) => (p === "assign" ? null : "assign"))}
                                    className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-xs font-medium text-ink-secondary transition hover:bg-surface-2"
                                >
                                    <User size={12} />
                                    {assignTo?.label?.split(" (")[0] || "Assign"}
                                    <ChevronDown size={10} />
                                </button>
                                {openPanel === "assign" && (
                                    <div className="absolute left-0 top-full z-20 mt-1 max-h-48 w-56 overflow-y-auto rounded-lg border border-line bg-surface-2 py-1 shadow-lg">
                                        {assignOptions.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => {
                                                    setAssignTo(opt);
                                                    setOpenPanel(null);
                                                }}
                                                className={`block w-full px-3 py-1.5 text-left text-xs transition hover:bg-surface-3 ${
                                                    assignTo?.value === opt.value
                                                        ? "text-primary font-semibold"
                                                        : "text-ink-secondary"
                                                }`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setOpenPanel((p) => (p === "priority" ? null : "priority"))}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium transition ${priorityLabelData?.className || "border-line text-ink-secondary"}`}
                            >
                                <Flag size={12} />
                                {priorityLabelData?.label}
                            </button>
                            {openPanel === "priority" && (
                                <div className="absolute left-0 top-full z-20 mt-1 rounded-lg border border-line bg-surface-2 py-1 shadow-lg">
                                    {PRIORITY_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => {
                                                setPriority(opt.value);
                                                setOpenPanel(null);
                                            }}
                                            className={`block w-full px-3 py-1.5 text-left text-xs transition hover:bg-surface-3 ${opt.className}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setOpenPanel((p) => (p === "repeat" ? null : "repeat"))}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium transition ${
                                    repetition !== "none"
                                        ? "border-primary bg-primary-soft text-primary"
                                        : "border-line text-ink-secondary hover:bg-surface-2"
                                }`}
                            >
                                <Repeat size={12} />
                                {repetition === "none" ? "Repeat" : repeatLabel}
                            </button>
                            {openPanel === "repeat" && (
                                <div className="absolute left-0 top-full z-20 mt-1 rounded-lg border border-line bg-surface-2 py-1 shadow-lg">
                                    {REPEAT_OPTIONS.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => {
                                                setRepetition(opt.value);
                                                setOpenPanel(null);
                                            }}
                                            className={`block w-full px-3 py-1.5 text-left text-xs transition hover:bg-surface-3 ${
                                                repetition === opt.value
                                                    ? "text-primary font-semibold"
                                                    : "text-ink-secondary"
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    {parsedPreview && (
                        <p className="mt-1.5 text-[11px] text-primary/90">
                            Parsed: {parsedPreview.title}
                            {parsedPreview.duePreset !== "today" && ` · due ${parsedPreview.duePreset}`}
                            {parsedPreview.priority !== "medium" && ` · ${parsedPreview.priority}`}
                            {parsedPreview.repetition !== "none" && ` · ${parsedPreview.repetition}`}
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleCreate}
                    disabled={submitting || !title.trim()}
                    className="mt-1 inline-flex shrink-0 items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-contrast transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                    {submitting ? "Adding…" : "Add"}
                    {!submitting && <CornerDownLeft size={12} />}
                </button>
            </div>
            <p className="mt-2 pl-6 text-[11px] text-ink-muted hidden sm:block">
                {quickAddHintText()}
            </p>
        </div>
    );
};

export default TaskQuickAdd;
